import { ROLES, userCreateSchema, userPasswordSchema, userUpdateSchema, type Role } from "@erp/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { KeyRound, MoreHorizontal, Pencil, Plus, ShieldCheck, UserCheck, UserX, Wand2 } from "lucide-react";
import { useState, type FormEvent } from "react";
import { toast } from "sonner";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Confirm } from "~/components/ui/confirm";
import { Field } from "~/components/ui/field";
import { Input } from "~/components/ui/input";
import { Menu, type MenuAction } from "~/components/ui/menu";
import { Panel } from "~/components/ui/page";
import { Select } from "~/components/ui/select";
import { Sheet } from "~/components/ui/sheet";
import { EmptyState, ErrorState, Skeleton } from "~/components/ui/states";
import { api, unwrap } from "~/lib/api";
import { errorMessageKey, fieldErrors } from "~/lib/errors";
import { errorAt, schemaErrors, type ErrorMap } from "~/lib/forms";
import { useFormat, useT } from "~/lib/i18n";
import { useSession } from "~/lib/session";
import { HelpTip } from "~/components/ui/help-tip";

export const Route = createFileRoute("/_app/settings/users")({ component: UsersPage });

const usersQuery = { queryKey: ["users"], queryFn: () => unwrap(api.users.get()) };
type User = Awaited<ReturnType<typeof usersQuery.queryFn>>[number];

/** Readable yet strong temporary password (no ambiguous characters). */
function generatePassword(): string {
  const alphabet = "abcdefghjkmnpqrstuvwxyzABCDEFGHJKMNPQRSTUVWXYZ23456789";
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  const chars = Array.from(bytes, (b) => alphabet[b % alphabet.length]);
  return [chars.slice(0, 4), chars.slice(4, 8), chars.slice(8, 12), chars.slice(12, 16)].map((g) => g.join("")).join("-");
}

type Dialog =
  | { kind: "none" }
  | { kind: "create" }
  | { kind: "edit"; user: User }
  | { kind: "password"; user: User }
  | { kind: "disable"; user: User };

function UsersPage() {
  const t = useT();
  const format = useFormat();
  const me = useSession();
  const queryClient = useQueryClient();
  const query = useQuery(usersQuery);
  const [dialog, setDialog] = useState<Dialog>({ kind: "none" });
  const close = () => setDialog({ kind: "none" });

  const ban = useMutation({
    mutationFn: ({ user, banned }: { user: User; banned: boolean }) => unwrap(api.users({ id: user.id }).ban.post({ banned })),
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: ["users"] });
      toast.success(saved.banned ? t("users.disabled") : t("users.enabled"));
      close();
    },
    onError: (error) => toast.error(t(errorMessageKey(error))),
  });

  const roleOptions = ROLES.map((r) => ({ value: r, label: t(`auth.roles.${r}`) }));

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button variant="primary" onClick={() => setDialog({ kind: "create" })}>
          <Plus />
          {t("users.new")}
        </Button>
      </div>
      <Panel>
        {query.isPending ? (
          <div className="flex flex-col gap-3 p-5">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : query.isError ? (
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        ) : query.data.length === 0 ? (
          <EmptyState title={t("users.empty")} />
        ) : (
          <ul className="divide-y divide-line">
            {query.data.map((user) => {
              const self = user.id === me.id;
              const actions: (MenuAction | "separator")[] = [
                { label: t("users.actions.edit"), icon: <Pencil />, onSelect: () => setDialog({ kind: "edit", user }) },
                {
                  label: t("users.actions.resetPassword"),
                  icon: <KeyRound />,
                  onSelect: () => setDialog({ kind: "password", user }),
                },
              ];
              if (!self)
                actions.push(
                  "separator",
                  user.banned
                    ? {
                        label: t("users.actions.enable"),
                        icon: <UserCheck />,
                        onSelect: () => ban.mutate({ user, banned: false }),
                      }
                    : {
                        label: t("users.actions.disable"),
                        icon: <UserX />,
                        tone: "danger",
                        onSelect: () => setDialog({ kind: "disable", user }),
                      },
                );
              return (
                <li key={user.id} className="flex items-center gap-3 px-5 py-3.5 md:px-6">
                  <div className={user.banned ? "min-w-0 flex-1 opacity-60" : "min-w-0 flex-1"}>
                    <p className="flex flex-wrap items-center gap-2 font-medium">
                      <span className="truncate">{user.name}</span>
                      {self ? <Badge tone="gold">{t("users.you")}</Badge> : null}
                      {user.banned ? <Badge tone="danger">{t("users.statuses.disabled")}</Badge> : null}
                      {user.twoFactorEnabled ? (
                        <ShieldCheck className="size-4 text-success" aria-label={t("users.fields.twoFactor")} />
                      ) : null}
                    </p>
                    <p className="truncate text-sm text-fg-2">{user.email}</p>
                    <p className="text-xs text-fg-3 md:hidden">
                      {user.lastSeenAt ? format.relative(user.lastSeenAt) : t("users.never")}
                    </p>
                  </div>
                  <div className="hidden w-40 shrink-0 text-sm text-fg-3 md:block">
                    {user.lastSeenAt ? format.relative(user.lastSeenAt) : t("users.never")}
                  </div>
                  <Badge className="shrink-0">{user.role ? t(`auth.roles.${user.role as Role}`) : t("common.states.none")}</Badge>
                  <Menu
                    trigger={
                      <Button variant="ghost" size="icon" aria-label={t("common.actions.more")}>
                        <MoreHorizontal />
                      </Button>
                    }
                    actions={actions}
                  />
                </li>
              );
            })}
          </ul>
        )}
      </Panel>

      <UserSheet dialog={dialog} onClose={close} roleOptions={roleOptions} />
      <PasswordSheet user={dialog.kind === "password" ? dialog.user : null} onClose={close} />
      <Confirm
        open={dialog.kind === "disable"}
        onOpenChange={(open) => (open ? undefined : close())}
        title={dialog.kind === "disable" ? t("users.disableTitle", { name: dialog.user.name }) : ""}
        body={t("users.disableBody")}
        confirmLabel={t("users.actions.disable")}
        tone="danger"
        loading={ban.isPending}
        onConfirm={() => (dialog.kind === "disable" ? ban.mutate({ user: dialog.user, banned: true }) : undefined)}
      />
    </div>
  );
}

const USER_FORM = "user-form";

function UserSheet({
  dialog,
  onClose,
  roleOptions,
}: {
  dialog: Dialog;
  onClose: () => void;
  roleOptions: { value: Role; label: string }[];
}) {
  const t = useT();
  const open = dialog.kind === "create" || dialog.kind === "edit";
  const user = dialog.kind === "edit" ? dialog.user : null;
  const [saving, setSaving] = useState(false);
  return (
    <Sheet
      open={open}
      onOpenChange={(next) => (next ? undefined : onClose())}
      title={user ? t("users.edit") : t("users.new")}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("common.actions.cancel")}
          </Button>
          <Button variant="primary" type="submit" form={USER_FORM} loading={saving}>
            {user ? t("common.actions.save") : t("common.actions.create")}
          </Button>
        </>
      }
    >
      {open ? (
        <UserForm key={user?.id ?? "new"} user={user} onDone={onClose} onSavingChange={setSaving} roleOptions={roleOptions} />
      ) : null}
    </Sheet>
  );
}

function UserForm({
  user,
  onDone,
  onSavingChange,
  roleOptions,
}: {
  user: User | null;
  onDone: () => void;
  onSavingChange: (saving: boolean) => void;
  roleOptions: { value: Role; label: string }[];
}) {
  const t = useT();
  const me = useSession();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState({
    name: user?.name ?? "",
    email: user?.email ?? "",
    role: ((user?.role as Role | null) ?? "sales") as Role,
    password: user ? "" : generatePassword(),
  });
  const [submitted, setSubmitted] = useState(false);
  const [serverErrors, setServerErrors] = useState<ErrorMap>({});
  const [confirmingRole, setConfirmingRole] = useState(false);
  const schema = user ? userUpdateSchema : userCreateSchema;
  const payload = user ? { name: draft.name, role: draft.role } : draft;

  const save = useMutation({
    mutationFn: () =>
      user
        ? unwrap(api.users({ id: user.id }).put(userUpdateSchema.parse(payload)))
        : unwrap(api.users.post(userCreateSchema.parse(payload))),
    onMutate: () => onSavingChange(true),
    onSettled: () => onSavingChange(false),
    onSuccess: () => {
      setConfirmingRole(false);
      void queryClient.invalidateQueries({ queryKey: ["users"] });
      toast.success(user ? t("users.updated") : t("users.created"));
      onDone();
    },
    onError: (error) => {
      setServerErrors(fieldErrors(error));
      toast.error(t(errorMessageKey(error)));
    },
  });

  const errors: ErrorMap = { ...serverErrors, ...(submitted ? schemaErrors(schema, payload) : {}) };
  const err = (path: string) => errorAt(t, errors, path);
  const submit = (event: FormEvent) => {
    event.preventDefault();
    setSubmitted(true);
    if (Object.keys(schemaErrors(schema, payload)).length) return;
    // A new role signs the person out everywhere: ask first.
    if (user && draft.role !== user.role) setConfirmingRole(true);
    else save.mutate();
  };
  const selfEdit = user?.id === me.id;

  return (
    <form id={USER_FORM} onSubmit={submit} noValidate className="flex flex-col gap-5">
      <Field label={t("users.fields.name")} error={err("name")}>
        <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} autoComplete="off" />
      </Field>
      <Field label={t("users.fields.email")} error={err("email")}>
        <Input
          type="email"
          inputMode="email"
          value={draft.email}
          disabled={Boolean(user)}
          onChange={(e) => setDraft({ ...draft, email: e.target.value })}
          autoComplete="off"
        />
      </Field>
      <Field
        label={
          <span className="inline-flex items-center gap-0.5">
            {t("users.fields.role")}
            <HelpTip id="settings.userRole" />
          </span>
        }
        hint={t(`users.roleHints.${draft.role}`)}
      >
        <Select
          value={draft.role}
          onValueChange={(role) => setDraft({ ...draft, role })}
          options={roleOptions}
          disabled={selfEdit}
        />
      </Field>
      {user ? null : (
        <PasswordField value={draft.password} onChange={(password) => setDraft({ ...draft, password })} error={err("password")} />
      )}
      {user ? (
        <Confirm
          open={confirmingRole}
          onOpenChange={setConfirmingRole}
          title={t("confirms.userRole.title", { name: user.name })}
          body={t("confirms.userRole.body", { role: t(`auth.roles.${draft.role}`) })}
          confirmLabel={t("confirms.userRole.confirm")}
          tone="danger"
          loading={save.isPending}
          onConfirm={() => save.mutate()}
        />
      ) : null}
    </form>
  );
}

function PasswordField({ value, onChange, error }: { value: string; onChange: (value: string) => void; error?: string }) {
  const t = useT();
  return (
    <Field label={t("users.fields.password")} hint={t("users.passwordHint")} error={error}>
      <div className="flex gap-2">
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete="new-password"
          className="font-mono tabular"
          spellCheck={false}
        />
        <Button
          type="button"
          variant="secondary"
          size="icon"
          aria-label={t("users.generate")}
          title={t("users.generate")}
          onClick={() => onChange(generatePassword())}
        >
          <Wand2 />
        </Button>
      </div>
    </Field>
  );
}

const PASSWORD_FORM = "password-form";

function PasswordSheet({ user, onClose }: { user: User | null; onClose: () => void }) {
  const t = useT();
  const [password, setPassword] = useState(generatePassword);
  const [submitted, setSubmitted] = useState(false);
  const [target, setTarget] = useState<string | null>(null);
  // Fresh suggestion each time the sheet opens for someone.
  if ((user?.id ?? null) !== target) {
    setTarget(user?.id ?? null);
    setPassword(generatePassword());
    setSubmitted(false);
  }
  const save = useMutation({
    mutationFn: (id: string) => unwrap(api.users({ id }).password.post(userPasswordSchema.parse({ password }))),
    onSuccess: async () => {
      try {
        await navigator.clipboard.writeText(password);
        toast.success(t("users.passwordSet"), { description: t("users.copied") });
      } catch {
        toast.success(t("users.passwordSet"));
      }
      onClose();
    },
    onError: (error) => toast.error(t(errorMessageKey(error))),
  });
  const errors: ErrorMap = submitted ? schemaErrors(userPasswordSchema, { password }) : {};
  const submit = (event: FormEvent) => {
    event.preventDefault();
    setSubmitted(true);
    if (user && Object.keys(schemaErrors(userPasswordSchema, { password })).length === 0) save.mutate(user.id);
  };
  return (
    <Sheet
      open={Boolean(user)}
      onOpenChange={(open) => (open ? undefined : onClose())}
      title={user ? t("users.resetTitle", { name: user.name }) : ""}
      description={t("users.resetHint")}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("common.actions.cancel")}
          </Button>
          <Button variant="primary" type="submit" form={PASSWORD_FORM} loading={save.isPending}>
            {t("common.actions.save")}
          </Button>
        </>
      }
    >
      <form id={PASSWORD_FORM} onSubmit={submit} noValidate>
        <PasswordField value={password} onChange={setPassword} error={errorAt(t, errors, "password")} />
      </form>
    </Sheet>
  );
}
